import axios from "axios";
import {environmentConfig} from "@/config/enviromentConfig";

const AUTHORIZE_URL = 'https://accounts.spotify.com/authorize';
const TOKEN_URL = 'https://accounts.spotify.com/api/token';
const API_URL = 'https://api.spotify.com/v1';
const SCOPE = 'user-library-modify';
const VERIFIER_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

const storageKeys = {
    accessToken: 'spotifyAccessToken',
    refreshToken: 'spotifyRefreshToken',
    expiresAt: 'spotifyExpiresAt',
    codeVerifier: 'spotifyCodeVerifier',
    authState: 'spotifyAuthState',
    pendingTrack: 'spotifyPendingTrack'
};

export default class SpotifyService {
    static isConfigured() {
        return !!environmentConfig.spotifyClientId && !!environmentConfig.spotifyRedirectUri;
    }

    static isConnected() {
        return !!localStorage.getItem(storageKeys.refreshToken);
    }

    static randomString(length) {
        const bytes = new Uint8Array(length);
        window.crypto.getRandomValues(bytes);
        return Array.from(bytes, byte => VERIFIER_ALPHABET[byte & 63]).join('');
    }

    static base64UrlEncode(buffer) {
        return btoa(String.fromCharCode(...new Uint8Array(buffer)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }

    static async codeChallenge(verifier) {
        const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
        return SpotifyService.base64UrlEncode(digest);
    }

    static async buildAuthoriseUrl() {
        const verifier = SpotifyService.randomString(64);
        const state = SpotifyService.randomString(16);
        localStorage.setItem(storageKeys.codeVerifier, verifier);
        localStorage.setItem(storageKeys.authState, state);
        const params = new URLSearchParams({
            client_id: environmentConfig.spotifyClientId,
            response_type: 'code',
            redirect_uri: environmentConfig.spotifyRedirectUri,
            code_challenge_method: 'S256',
            code_challenge: await SpotifyService.codeChallenge(verifier),
            state: state,
            scope: SCOPE
        });
        return `${AUTHORIZE_URL}?${params.toString()}`;
    }

    static setPendingTrack(track) {
        localStorage.setItem(storageKeys.pendingTrack, JSON.stringify(track));
    }

    static takePendingTrack() {
        const raw = localStorage.getItem(storageKeys.pendingTrack);
        localStorage.removeItem(storageKeys.pendingTrack);
        try {
            return raw ? JSON.parse(raw) : null;
        } catch (error) {
            return null;
        }
    }

    static storeTokens(tokenResponse) {
        localStorage.setItem(storageKeys.accessToken, tokenResponse.access_token);
        localStorage.setItem(storageKeys.expiresAt, `${Date.now() + (tokenResponse.expires_in - 60) * 1000}`);
        if (tokenResponse.refresh_token) localStorage.setItem(storageKeys.refreshToken, tokenResponse.refresh_token);
    }

    static async postToken(body) {
        const response = await axios.post(TOKEN_URL, new URLSearchParams(body), {
            headers: {'Content-Type': 'application/x-www-form-urlencoded'}
        });
        SpotifyService.storeTokens(response.data);
        return response.data.access_token;
    }

    static async exchangeCodeForToken(code, state) {
        const expectedState = localStorage.getItem(storageKeys.authState);
        const verifier = localStorage.getItem(storageKeys.codeVerifier);
        localStorage.removeItem(storageKeys.authState);
        localStorage.removeItem(storageKeys.codeVerifier);
        if (!code || !verifier || !expectedState || state !== expectedState) {
            throw new Error('Spotify authorisation could not be verified');
        }
        return await SpotifyService.postToken({
            grant_type: 'authorization_code',
            code: code,
            redirect_uri: environmentConfig.spotifyRedirectUri,
            client_id: environmentConfig.spotifyClientId,
            code_verifier: verifier
        });
    }

    static async accessToken() {
        const accessToken = localStorage.getItem(storageKeys.accessToken);
        const expiresAt = Number(localStorage.getItem(storageKeys.expiresAt));
        if (accessToken && expiresAt > Date.now()) return accessToken;
        const refreshToken = localStorage.getItem(storageKeys.refreshToken);
        if (!refreshToken) return null;
        return await SpotifyService.postToken({
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            client_id: environmentConfig.spotifyClientId
        });
    }

    static sanitiseTerm(term) {
        return `${term ?? ''}`.replace(/["\\]/g, ' ').trim();
    }

    static trackSearchQuery({isrc, title, artist}) {
        if (isrc) return `isrc:${SpotifyService.sanitiseTerm(isrc)}`;
        const cleanTitle = SpotifyService.sanitiseTerm(title);
        if (!cleanTitle) return null;
        const cleanArtist = SpotifyService.sanitiseTerm(artist);
        if (cleanArtist) return `track:"${cleanTitle}" artist:"${cleanArtist}"`;
        return `track:"${cleanTitle}"`;
    }

    static async findTrackId(track, accessToken) {
        const query = SpotifyService.trackSearchQuery(track);
        if (!query) return null;
        const params = new URLSearchParams({q: query, type: 'track', limit: '1', market: 'ZA'});
        const response = await axios.get(`${API_URL}/search?${params.toString()}`, {
            headers: {Authorization: `Bearer ${accessToken}`}
        });
        return response.data?.tracks?.items?.[0]?.id ?? null;
    }

    static async addTrackToLikedSongs(track) {
        const accessToken = await SpotifyService.accessToken();
        if (!accessToken) return {added: false, reason: 'not-connected'};
        const trackId = await SpotifyService.findTrackId(track, accessToken);
        if (!trackId) return {added: false, reason: 'not-found'};
        const params = new URLSearchParams({uris: `spotify:track:${trackId}`});
        await axios.put(`${API_URL}/me/library?${params.toString()}`, {}, {
            headers: {Authorization: `Bearer ${accessToken}`}
        });
        return {added: true, trackId: trackId};
    }

    static disconnect() {
        Object.values(storageKeys).forEach(key => localStorage.removeItem(key));
    }
}
